import React, { useState, useEffect } from "react";
import { Link, withRouter } from "react-router-dom";
import { Nav, Navbar, NavItem, NavDropdown } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import "./App.css";
import Routes from "./Routes";
import { fetchAuthSession, signOut, fetchUserAttributes } from 'aws-amplify/auth';
import { getUrl } from 'aws-amplify/storage';
import { Amplify } from 'aws-amplify';
import config from './config';
import { useTheme, THEMES, THEME_NAMES } from './contexts/ThemeContext';
import { useInactivityLogout } from './hooks/useInactivityLogout';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.USER_POOL_ID,
      userPoolClientId: config.cognito.APP_CLIENT_ID,
      identityPoolId: config.cognito.IDENTITY_POOL_ID,
      region: config.cognito.REGION
    }
  },
  Storage: {
    S3: {
      bucket: config.s3.BUCKET,
      region: config.s3.REGION,
      defaultAccessLevel: 'guest'
    }
  },
  API: {
    REST: {
      logindetails: {
        endpoint: config.apiGateway.URL,
        region: config.apiGateway.REGION
      }
    }
  },
  ssr: false
});


// Helper to generate Gravatar URL
function getGravatarUrl(email) {
  if (!email) return '';
  
  // Simple MD5 hash implementation for browser
  const md5 = (string) => {
    const crypto = require('crypto-js');
    return crypto.MD5(string.toLowerCase().trim()).toString();
  };
  
  try {
    const hash = md5(email);
    return `https://www.gravatar.com/avatar/${hash}?d=identicon&s=32`;
  } catch (e) {
    // Fallback to a default avatar if crypto-js is not available
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(email.split('@')[0])}&size=32&background=random`;
  }
}

function App(props) {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, userHasAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userPhotoUrl, setUserPhotoUrl] = useState("");
  const { theme, isAutoMode, changeTheme, enableAutoMode } = useTheme();
  
  // Auto-logout after 15 minutes of inactivity
  useInactivityLogout(async () => {
    if (isAuthenticated) {
      await signOut();
      userHasAuthenticated(false);
      alert('You have been logged out due to inactivity.');
      props.history.push("/login");
    }
  }, 15);
  
  
  useEffect(() => {
    onLoad();
  }, []);
  async function onLoad() {
    try {
      const session = await fetchAuthSession();
      console.log('Session fetched:', {
        hasCredentials: !!session.credentials,
        hasTokens: !!session.tokens,
        identityId: session.identityId
      });
      
      // Only set authenticated if we have valid credentials
      if (session.tokens) {
        userHasAuthenticated(true);
        
        // Fetch user attributes to get email and photo
        try {
          const attributes = await fetchUserAttributes();
          if (attributes.email) {
            setUserEmail(attributes.email);
          }
          
          // Load custom profile photo if exists
          if (attributes.picture) {
            try {
              const result = await getUrl({
                path: attributes.picture
              });
              setUserPhotoUrl(result.url.toString());
            } catch (photoError) {
              console.log('No custom photo found');
            }
          }
        } catch (attrError) {
          console.error('Error fetching user attributes:', attrError);
        }
      }
    }
    catch(e) {
      console.log('Auth session error:', e);
      if (e !== 'No current user' && e.message !== 'No current user') {
        console.error('Unexpected auth error:', e);
      }
    }
    setIsAuthenticating(false);
  }
  async function handleLogout() {
    await signOut();
    userHasAuthenticated(false);
    props.history.push("/login");
  }
  return (
    !isAuthenticating && (
      <div className="App container">
        <Navbar fluid collapseOnSelect>
          <Navbar.Header>
            <Navbar.Brand>
              <Link to="/">Scratch</Link>
            </Navbar.Brand>
            <Navbar.Toggle />
          </Navbar.Header>
          <Navbar.Collapse>
            <Nav pullRight>
              <NavDropdown 
                eventKey={1} 
                title={`🎨 ${THEME_NAMES[theme]} ${isAutoMode ? '⏰' : ''}`} 
                id="theme-dropdown"
              >
                {Object.values(THEMES).map((themeOption) => (
                  <NavItem 
                    key={themeOption}
                    onClick={() => changeTheme(themeOption)}
                    active={theme === themeOption && !isAutoMode}
                  >
                    {THEME_NAMES[themeOption]} {theme === themeOption && !isAutoMode && '✓'}
                  </NavItem>
                ))}
                <NavItem divider />
                <NavItem onClick={enableAutoMode} active={isAutoMode}>
                  Auto Mode (Time-based) {isAutoMode && '✓'}
                </NavItem>
              </NavDropdown>
              {isAuthenticated ? (
                <>
                  <LinkContainer to="/password-generator">
                    <NavItem>Password Generator</NavItem>
                  </LinkContainer>
                  <LinkContainer to="/import-export">
                    <NavItem>Import/Export</NavItem>
                  </LinkContainer>
                  <NavDropdown 
                    eventKey={3} 
                    title={
                      userEmail && (
                        <img 
                          src={userPhotoUrl || getGravatarUrl(userEmail)}
                          alt="Profile"
                          style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid var(--border-color)'
                          }}
                        />
                      )
                    } 
                    id="basic-nav-dropdown"
                    noCaret
                  >
                    <LinkContainer to="/profile">
                      <NavItem>Profile Photo</NavItem>
                    </LinkContainer>
                    <LinkContainer to="/changepassword">
                      <NavItem>Change password</NavItem>
                    </LinkContainer>
                    <LinkContainer to="/settings">
                      <NavItem>Settings</NavItem>
                    </LinkContainer>
                    <NavItem onClick={handleLogout}>Logout</NavItem>
                  </NavDropdown>
                </>
              ) : (
                <>
                  <LinkContainer to="/signup">
                    <NavItem>Signup</NavItem>
                  </LinkContainer>
                  <LinkContainer to="/login">
                    <NavItem>Login</NavItem>
                  </LinkContainer>
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Navbar>
        <Routes appProps={{ isAuthenticated, userHasAuthenticated }} />
      </div>
    )
  );
}

export default withRouter(App);